import { Program, AnchorProvider, BN } from "@coral-xyz/anchor";
import { IDL, Redemption } from "./redemption";
import {
  Connection,
  Keypair,
  PublicKey,
  SystemProgram,
  TransactionInstruction,
} from "@solana/web3.js";
import NodeWallet from "@coral-xyz/anchor/dist/cjs/nodewallet";
import { findATAAddrSync, uiToNative } from "@uxd-protocol/uxd-client";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";

export type ProgramState = {
  realm: PublicKey;
  realmUsdc: PublicKey;
  uctMint: PublicKey;
  uxpMint: PublicKey;
  usdcMint: PublicKey;
  uxpInitialSupply: BN;
  phaseOneEndTimestamp: number;
  phaseTwoEndTimestamp?: number;
  phaseOneTotalUsdcRedeemableAmount: BN;
  totalBurntUxpAmount: BN;
  outstandingUctAmount: BN;
  phaseOneRedeemedUsdcAmount: BN;
  phaseTwoTotalRedeemableUsdcAmount: BN;
  redeemedUctAmount: BN;
};

export type ClaimAccountState = {
  burntUctAmount: BN;
  phaseOneRedeemedUsdcAmount: BN;
  phaseTwoRedeemedUsdcAmount: BN;
};

export class RedemptionClient {
  public readonly readonlyProgram: Program<Redemption>;
  protected program: Program<Redemption> | null;
  public state: ProgramState;
  public claimAccountState: ClaimAccountState | null;

  static async initialize(programId: PublicKey, connection: Connection) {
    const readonlyProgram = new Program(
      IDL,
      programId,
      new AnchorProvider(
        connection,
        // Generate new wallet - only readonly
        new NodeWallet(new Keypair()),
        AnchorProvider.defaultOptions()
      )
    );
    const [realm] = await PublicKey.findProgramAddress(
      [Buffer.from("realm")],
      programId
    );

    const state = await RedemptionClient.setProgramRealmState(
      readonlyProgram,
      realm
    );
    return new RedemptionClient(readonlyProgram, state);
  }
  public static async setProgramRealmState(
    program: Program<Redemption>,
    realmAddress: PublicKey
  ): Promise<ProgramState> {
    const realmInfo = await program.account.realm.fetch(realmAddress);
    return {
      realm: realmAddress,
      realmUsdc: realmInfo.realmUsdc,
      uctMint: realmInfo.uctMint,
      uxpMint: realmInfo.uxpMint,
      usdcMint: realmInfo.usdcMint,
      phaseOneEndTimestamp: realmInfo.phaseOneEndTimestampSeconds.toNumber(),
      phaseTwoEndTimestamp: realmInfo.phaseTwoEndTimestampSeconds.toNumber(),
      phaseOneTotalUsdcRedeemableAmount:
        realmInfo.phaseOneTotalRedeemableUsdcAmount,
      totalBurntUxpAmount: realmInfo.burntUxpAmount,
      phaseOneRedeemedUsdcAmount: realmInfo.phaseOneRedeemedUsdcAmount,
      phaseTwoTotalRedeemableUsdcAmount:
        realmInfo.phaseTwoTotalRedeemableUsdcAmount,
      outstandingUctAmount: realmInfo.outstandingUctAmount,
      uxpInitialSupply: realmInfo.uxpInitialSupply,
      redeemedUctAmount: realmInfo.redeemedUctAmount,
    };
  }

  public static async setClaimAccountState(
    program: Program<Redemption>,
    walletKey: PublicKey
  ): Promise<ClaimAccountState> {
    const [claimAccountAddr] = PublicKey.findProgramAddressSync(
      [Buffer.from("claim_account"), walletKey.toBuffer()],
      program.programId
    );

    const claimAccount = await program.account.claimAccount.fetch(
      claimAccountAddr
    );
    return {
      burntUctAmount: claimAccount.burntUctAmount,
      phaseOneRedeemedUsdcAmount: claimAccount.phaseOneRedeemedUsdcAmount,
      phaseTwoRedeemedUsdcAmount: claimAccount.phaseTwoRedeemedUsdcAmount,
    };
  }

  constructor(readonlyProgram: Program<Redemption>, state: ProgramState) {
    this.program = null;
    this.claimAccountState = null;
    this.readonlyProgram = readonlyProgram;
    this.state = state;
  }

  public getProgram() {
    return this.program;
  }

  public setProgram(program: Program<Redemption> | null) {
    this.program = program;
  }

  public async convertUxpToUct({
    uxpAmount,
    walletKey,
  }: {
    walletKey: PublicKey;
    uxpAmount: number;
  }): Promise<TransactionInstruction> {
    if (!this.program) throw new Error("Not connected");
    const [userUct] = findATAAddrSync(walletKey, this.state.uctMint);
    const [userUxp] = findATAAddrSync(walletKey, this.state.uxpMint);
    return this.program.methods
      .convertUxpToUct(new BN((uxpAmount * 10 ** 9).toString()))
      .accountsStrict({
        payer: walletKey,
        user: walletKey,
        userUxp: userUxp,
        userUct: userUct,
        realm: this.state.realm,
        uctMint: this.state.uctMint,
        uxpMint: this.state.uxpMint,
        systemProgram: SystemProgram.programId,
        tokenProgram: TOKEN_PROGRAM_ID,
      })
      .instruction();
  }

  public async redeemPhaseOne({
    amount,
    walletKey,
  }: {
    amount: number;
    walletKey: PublicKey;
  }): Promise<TransactionInstruction> {
    if (!this.program) throw new Error("Not connected");

    const [userUct] = findATAAddrSync(walletKey, this.state.uctMint);
    const [userUsdc] = findATAAddrSync(walletKey, this.state.usdcMint);
    const [claimAccount] = PublicKey.findProgramAddressSync(
      [Buffer.from("claim_account"), walletKey.toBuffer()],
      this.program.programId
    );

    return this.program.methods
      .redeemPhaseOne(new BN((amount * 10 ** 9).toString()))
      .accountsStrict({
        claimAccount,
        realm: this.state.realm,
        realmUsdc: this.state.realmUsdc,
        uctMint: this.state.uctMint,
        payer: walletKey,
        user: walletKey,
        userUct: userUct,
        userUsdc: userUsdc,
        systemProgram: SystemProgram.programId,
        tokenProgram: TOKEN_PROGRAM_ID,
      })
      .instruction();
  }

  public redeemPhaseTwo(usdcAmount: number, walletKey: PublicKey) {
    if (!this.program) throw new Error("Not connected");
    const [userUsdc] = findATAAddrSync(walletKey, this.state.usdcMint);
    const [claimAccount] = PublicKey.findProgramAddressSync(
      [Buffer.from("claim_account"), walletKey.toBuffer()],
      this.program.programId
    );
    return this.program.methods
      .redeemPhaseTwo(new BN(Math.floor(usdcAmount * 10 ** 6).toString()))
      .accountsStrict({
        payer: walletKey,
        user: walletKey,
        userUsdc,
        realm: this.state.realm,
        realmUsdc: this.state.realmUsdc,
        claimAccount,
        systemProgram: SystemProgram.programId,
        tokenProgram: TOKEN_PROGRAM_ID,
      })
      .instruction();
  }

  public async refreshProgramState(): Promise<ProgramState> {
    this.state = await RedemptionClient.setProgramRealmState(
      this.readonlyProgram,
      this.state.realm
    );
    return this.state;
  }

  public async refreshClaimAccountState(): Promise<ClaimAccountState> {
    if (!this.program || !this.program.provider.publicKey)
      throw new Error("Not connected");
    const walletKey = this.program.provider.publicKey;
    this.claimAccountState = await RedemptionClient.setClaimAccountState(
      this.program,
      walletKey
    );

    return this.claimAccountState;
  }
}
